import { action, readonly } from '@reatom/framework'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { createCurrencyBalanceAtom } from '@root/features/currency/currency-input-reatom'
import { reatomCreateLimitOrder } from '@root/features/trade/limit-orders'
import { tradeSettingsStateAtom } from '@root/features/trade/trade-settings'
import {
  tradingPairAtom,
  tradingPairBaseAtom,
  tradingWalletAtom,
} from '@root/features/trade/trading/atoms'
import { Error } from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { reatomLimitBuyInput } from './reatom-limit-buy-input'

import type { Atom } from '@reatom/framework'
import type { TradeSettings } from '@root/entities/trade-settings'
import type BigNumber from 'bignumber.js'

export const reatomLimitBuyControls = ({
  buyAmountNativeAtom,
}: {
  buyAmountNativeAtom: Atom<BigNumber>
}) => {
  const {
    targetTypeAtom,
    targetPriceUsdAtom,
    targetMarketCapUsdAtom,
    setTargetTypeAction,
    setTargetRawValueAction,
    receiveAtom,
    percentageAtom,
    setPercentageAction,
    outputSettingsAtom,
  } = reatomLimitBuyInput({
    pairAtom: tradingPairAtom,
    buyAmountNativeAtom: buyAmountNativeAtom,
  })

  const balanceAtom = createCurrencyBalanceAtom(
    tradingPairBaseAtom,
    tradingWalletAtom,
  )

  // eslint-disable-next-line @reatom/unit-naming-rule
  const createModel = reatomCreateLimitOrder()

  const createOrderAction = action(
    async (
      ctx,
      {
        settingsOverride,
      }: { settingsOverride: TradeSettings.TradeSettings | undefined },
    ) => {
      const pair = ctx.get(tradingPairAtom)
      const wallet = ctx.get(tradingWalletAtom)

      const settings = settingsOverride ?? ctx.get(tradeSettingsStateAtom).value
      const limitOrderSettings = ctx.get(outputSettingsAtom)

      const amountNative = ctx.get(buyAmountNativeAtom)

      if (
        !pair ||
        !wallet ||
        !settings ||
        !limitOrderSettings ||
        amountNative.isZero()
      ) {
        return Result.fail(Error.UnknownError.fromUnknown('Invalid input'))
      }
      const network = ctx.get(selectedNetworkAtom)

      const result = await ctx.schedule(() =>
        createModel.createOrderAction(ctx, {
          type: 'buy',
          currencyAddress: pair.quote.address,
          price: ctx.get(pair.price),
          settings: settings,
          network: network,
          pairAddress: pair.address,
          walletId: wallet.id,
          marketCapUsd: ctx.get(pair.marketCapUsd),
          walletAddress: wallet.address,
          valueLow: limitOrderSettings.valueLow,
          valueHigh: limitOrderSettings.valueHigh,
          triggerType: limitOrderSettings.type,
          amount: amountNative,
          deadline: settings.deadline,
          provider: pair.provider,
        }),
      )

      if (Result.isSuccess(result)) {
        showSnackbarAction(ctx, {
          text: i18next.t('trading.limit-order.order-placed'),
          intent: 'success',
        })
      } else {
        showSnackbarAction(ctx, {
          text: i18next.t('trading.limit-order.order-error'),
          intent: 'error',
        })
      }

      return result
    },
    'createOrderAction',
  )

  return {
    targetTypeAtom: targetTypeAtom.pipe(readonly),
    setTargetTypeAction,
    targetPriceUsdAtom,
    targetMarketCapUsdAtom,
    setTargetRawValueAction,
    receiveAtom,
    balanceAtom,
    percentageAtom,
    setPercentageAction,
    createOrderRequestAtom: createModel.createStateAtom,
    createOrderAction,
  }
}
